"use client";

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!audioContext) {
    audioContext = new AudioContext();
  }
  return audioContext;
}

// Call this from inside a user gesture (e.g. the Spin click) so the
// browser unlocks audio before the RAF-driven ticks start firing.
export function unlockAudio() {
  const ctx = getAudioContext();
  if (ctx && ctx.state === "suspended") {
    ctx.resume();
  }
}

function playTone(
  ctx: AudioContext,
  frequency: number,
  duration: number,
  type: OscillatorType = "sine",
  delay = 0,
  volume = 0.15,
  endFrequency?: number,
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  const startTime = ctx.currentTime + delay;
  osc.frequency.setValueAtTime(frequency, startTime);
  if (endFrequency !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(endFrequency, startTime + duration);
  }
  osc.connect(gain);
  gain.connect(ctx.destination);

  gain.gain.setValueAtTime(volume, startTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

  osc.start(startTime);
  osc.stop(startTime + duration);
}

// A single mechanical "click" — fired once per peg the ticker passes.
// strength (0..1) scales the click brightness so fast early ticks are
// snappier and the final slow ticks are deeper and more satisfying.
export function playTick(strength = 1) {
  const ctx = getAudioContext();
  if (!ctx) return;
  const baseFreq = 1300 - strength * 500;
  playTone(ctx, baseFreq, 0.045, "triangle", 0, 0.05 + strength * 0.06, baseFreq * 0.5);
}

export function playResultSound(positive: boolean) {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (positive) {
    playTone(ctx, 523.25, 0.18, "triangle", 0, 0.18);
    playTone(ctx, 659.25, 0.18, "triangle", 0.09, 0.18);
    playTone(ctx, 783.99, 0.18, "triangle", 0.18, 0.18);
    playTone(ctx, 1046.5, 0.5, "triangle", 0.27, 0.22);
  } else {
    playTone(ctx, 392, 0.22, "sawtooth", 0, 0.12);
    playTone(ctx, 311.13, 0.25, "sawtooth", 0.14, 0.12);
    playTone(ctx, 196, 0.45, "sawtooth", 0.28, 0.14);
  }
}

export function playChaosSound() {
  const ctx = getAudioContext();
  if (!ctx) return;

  playTone(ctx, 110, 0.55, "sine", 0, 0.2);
  playTone(ctx, 220, 0.1, "sawtooth", 0, 0.15);
  playTone(ctx, 277.18, 0.1, "sawtooth", 0.08, 0.15);
  playTone(ctx, 329.63, 0.1, "sawtooth", 0.16, 0.15);
  playTone(ctx, 440, 0.1, "sawtooth", 0.24, 0.15);
  playTone(ctx, 554.37, 0.45, "sawtooth", 0.32, 0.18);
}

// === Fire (wheel "on fire" streak) ===
// All synthesized from filtered white noise — no audio assets. A looping
// low-passed noise roar whose volume/brightness scales with the heat tier, plus
// a one-shot whoosh + sub-bass swell when the fire flares up a tier.

let noiseBuffer: AudioBuffer | null = null;
function getNoiseBuffer(ctx: AudioContext): AudioBuffer {
  if (noiseBuffer && noiseBuffer.sampleRate === ctx.sampleRate) return noiseBuffer;
  const length = ctx.sampleRate * 2;
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  noiseBuffer = buffer;
  return buffer;
}

// One-shot "fwoosh" + low swell, played when the blaze jumps up a tier.
export function playFireWhoosh(level = 1) {
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  const src = ctx.createBufferSource();
  src.buffer = getNoiseBuffer(ctx);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.value = 0.8;
  bp.frequency.setValueAtTime(280, now);
  bp.frequency.exponentialRampToValueAtTime(1600 + level * 700, now + 0.32);
  const gain = ctx.createGain();
  const vol = 0.12 + level * 0.05;
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(vol, now + 0.07);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.62);
  src.connect(bp);
  bp.connect(gain);
  gain.connect(ctx.destination);
  src.start(now);
  src.stop(now + 0.66);

  // Anime "power-up" sub-bass swell under the whoosh.
  playTone(ctx, 64, 0.5, "sine", 0, 0.1 + level * 0.04, 150);
}

// Persistent fire roar that tracks the current heat tier (0 = off). Lazily
// created on first use and gain-ramped so tier changes crossfade smoothly.
let fireSource: AudioBufferSourceNode | null = null;
let fireGain: GainNode | null = null;
let fireFilter: BiquadFilterNode | null = null;

export function setFireRoar(level: number) {
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  if (level <= 0) {
    if (fireGain) fireGain.gain.setTargetAtTime(0.0001, now, 0.18);
    if (fireSource) {
      try {
        fireSource.stop(now + 0.7);
      } catch {
        /* already stopped */
      }
    }
    fireSource = null;
    fireGain = null;
    fireFilter = null;
    return;
  }

  if (!fireSource) {
    fireSource = ctx.createBufferSource();
    fireSource.buffer = getNoiseBuffer(ctx);
    fireSource.loop = true;
    fireFilter = ctx.createBiquadFilter();
    fireFilter.type = "lowpass";
    fireFilter.frequency.value = 600;
    fireGain = ctx.createGain();
    fireGain.gain.value = 0.0001;
    fireSource.connect(fireFilter);
    fireFilter.connect(fireGain);
    fireGain.connect(ctx.destination);
    fireSource.start();
  }
  // Subtle — sits under the music, swells with the tier. (Both nodes are
  // guaranteed set above — either they already existed or were just created.)
  fireGain!.gain.setTargetAtTime(0.014 + level * 0.016, now, 0.25);
  fireFilter!.frequency.setTargetAtTime(480 + level * 460, now, 0.3);
}
